import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Table from "@/components/Base/Table";
import Button from "@/components/Base/Button";
import { FormCheck, FormInput } from "@/components/Base/Form";
import Lucide from "@/components/Base/Lucide";
import { getToken } from "@/utils/auth";

type Product = {
  id: string;
  name: string;
  sku?: string | null;
  type?: string | null;
  status: boolean;
  displayOrder?: number | null;
  createdAt?: string;
};

type SettingRow = {
  productId: string;
  allowTradeIn?: boolean;
  tradeInValue?: string | number;
  amount?: string | number; // legacy alias from API
};

const PRODUCT_TYPE_ORDER = ["Cigarette", "Device", "Refills", "Accessories"] as const;
const COL_COUNT = 4;

function moneyToString(value: number) {
  if (!Number.isFinite(value)) return "0.00";
  return (Math.round(value * 100) / 100).toFixed(2);
}

function normalizeMoneyInput(input: string) {
  const cleaned = input.replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length === 1) return parts[0];
  return `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
}

function moneyToDigits(value: string) {
  return (value || "").replace(/[^\d]/g, "");
}

function digitsToMoney(valueDigits: string) {
  const digits = (valueDigits || "").replace(/[^\d]/g, "");
  const padded = digits.padStart(3, "0");
  const major = padded.slice(0, -2).replace(/^0+(?=\d)/, "") || "0";
  const minor = padded.slice(-2);
  return `${major}.${minor}`;
}

function nextAtmMoneyValue(current: string, key: string, maxDigits = 9) {
  const digits = moneyToDigits(current);
  if (/^\d$/.test(key)) {
    const appended = `${digits}${key}`.slice(-maxDigits);
    return digitsToMoney(appended);
  }
  if (key === "Backspace" || key === "Delete") {
    const trimmed = digits.slice(0, -1);
    return digitsToMoney(trimmed);
  }
  return current;
}

function sortProducts(a: Product, b: Product) {
  const typeA = (a.type || "Other").toLowerCase();
  const typeB = (b.type || "Other").toLowerCase();
  if (typeA !== typeB) return typeA.localeCompare(typeB);

  const ao = a.displayOrder;
  const bo = b.displayOrder;
  if (ao !== null && ao !== undefined && bo !== null && bo !== undefined) return ao - bo;
  if (ao !== null && ao !== undefined && (bo === null || bo === undefined)) return -1;
  if ((ao === null || ao === undefined) && bo !== null && bo !== undefined) return 1;
  const at = a.createdAt ? new Date(a.createdAt).getTime() : 0;
  const bt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
  if (at !== bt) return bt - at;
  return (a.name || "").localeCompare(b.name || "");
}

export default function TradeInPage() {
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const token = getToken();

  const [products, setProducts] = useState<Product[]>([]);
  const [allowByProductId, setAllowByProductId] = useState<Record<string, boolean>>({});
  const [initialAllowByProductId, setInitialAllowByProductId] = useState<Record<string, boolean>>({});
  const [valuesByProductId, setValuesByProductId] = useState<Record<string, string>>({});
  const [initialByProductId, setInitialByProductId] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expandedTypeNames, setExpandedTypeNames] = useState<Set<string>>(new Set());
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const productsRes = await axios.get(`${apiUrl}/products?pageSize=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const allProducts: Product[] = Array.isArray(productsRes.data?.data) ? productsRes.data.data : [];
        const nextProducts = allProducts.filter((p) => p.status !== false).sort(sortProducts);
        setProducts(nextProducts);

        if (nextProducts.length) {
          const productIds = nextProducts.map((p) => p.id);
          const settingsRes = await axios.get(`${apiUrl}/trade-ins?productIds=${productIds.join(",")}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const rows: SettingRow[] = Array.isArray(settingsRes.data?.data) ? settingsRes.data.data : [];

          const nextAllow: Record<string, boolean> = {};
          const nextValues: Record<string, string> = {};
          for (const p of nextProducts) {
            nextAllow[p.id] = false;
            nextValues[p.id] = "0.00";
          }
          for (const r of rows) {
            nextAllow[r.productId] = Boolean(r.allowTradeIn);
            nextValues[r.productId] = moneyToString(Number(r.tradeInValue ?? r.amount ?? 0));
          }
          setAllowByProductId(nextAllow);
          setInitialAllowByProductId(nextAllow);
          setValuesByProductId(nextValues);
          setInitialByProductId(nextValues);
        } else {
          setAllowByProductId({});
          setInitialAllowByProductId({});
          setValuesByProductId({});
          setInitialByProductId({});
        }
      } catch {
        setError("Failed to load trade-in data.");
      } finally {
        setLoading(false);
      }
    })();
  }, [apiUrl, token]);

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    setIsSubmitting(true);
    try {
      const entries = products.map((p) => {
        const allowed = Boolean(allowByProductId[p.id]);
        const raw = valuesByProductId[p.id] ?? "0.00";
        return {
          productId: p.id,
          allowTradeIn: allowed,
          tradeInValue: allowed ? Number(normalizeMoneyInput(raw) || "0") || 0 : 0,
        };
      });

      await axios.post(
        `${apiUrl}/trade-ins/bulk-upsert`,
        { entries },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const syncedValues: Record<string, string> = {};
      for (const p of products) {
        syncedValues[p.id] = allowByProductId[p.id] ? valuesByProductId[p.id] ?? "0.00" : "0.00";
      }
      setValuesByProductId(syncedValues);
      setInitialByProductId(syncedValues);
      setInitialAllowByProductId(allowByProductId);
      setIsEditing(false);
      setSuccess("Trade In values submitted successfully.");
    } catch {
      setError("Failed to submit trade-in values.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetAll = () => {
    const nextAllow: Record<string, boolean> = {};
    const nextValues: Record<string, string> = {};
    for (const p of products) {
      nextAllow[p.id] = false;
      nextValues[p.id] = "0.00";
    }
    setAllowByProductId(nextAllow);
    setValuesByProductId(nextValues);
  };

  const hasChanges = useMemo(() => {
    const keys = new Set([
      ...Object.keys(valuesByProductId),
      ...Object.keys(initialByProductId),
      ...Object.keys(allowByProductId),
      ...Object.keys(initialAllowByProductId),
    ]);
    for (const k of keys) {
      if ((valuesByProductId[k] ?? "") !== (initialByProductId[k] ?? "")) return true;
      if (Boolean(allowByProductId[k]) !== Boolean(initialAllowByProductId[k])) return true;
    }
    return false;
  }, [valuesByProductId, initialByProductId, allowByProductId, initialAllowByProductId]);

  const groupedProducts = useMemo(() => {
    const groups = new Map<string, Product[]>();
    for (const p of products) {
      const typeName = (p.type || "Other").trim() || "Other";
      const arr = groups.get(typeName) ?? [];
      arr.push(p);
      groups.set(typeName, arr);
    }
    return Array.from(groups.entries()).sort((a, b) => {
      const ai = PRODUCT_TYPE_ORDER.indexOf(a[0] as (typeof PRODUCT_TYPE_ORDER)[number]);
      const bi = PRODUCT_TYPE_ORDER.indexOf(b[0] as (typeof PRODUCT_TYPE_ORDER)[number]);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a[0].localeCompare(b[0]);
    });
  }, [products]);

  const visibleProductIds = useMemo(() => {
    const ids: string[] = [];
    for (const [typeName, typeProducts] of groupedProducts) {
      if (!expandedTypeNames.has(typeName)) continue;
      for (const p of typeProducts) ids.push(p.id);
    }
    return ids;
  }, [groupedProducts, expandedTypeNames]);

  const rowIndexByProductId = useMemo(() => {
    const m: Record<string, number> = {};
    visibleProductIds.forEach((id, idx) => {
      m[id] = idx;
    });
    return m;
  }, [visibleProductIds]);

  const inputId = (rowIndex: number) => `tradein-${rowIndex}`;
  const focusVertical = (rowIndex: number, delta: 1 | -1) => {
    let r = rowIndex + delta;
    while (r >= 0 && r < visibleProductIds.length) {
      const productId = visibleProductIds[r];
      if (!allowByProductId[productId]) {
        r += delta;
        continue;
      }
      const el = inputRefs.current[inputId(r)];
      if (el && !el.disabled) {
        el.focus();
        el.select?.();
        return true;
      }
      r += delta;
    }
    return false;
  };

  useEffect(() => {
    setExpandedTypeNames((prev) => {
      const next = new Set(prev);
      for (const [typeName] of groupedProducts) {
        if (!next.has(typeName)) next.add(typeName);
      }
      return next;
    });
  }, [groupedProducts]);

  const toggleType = (typeName: string) => {
    setExpandedTypeNames((prev) => {
      const next = new Set(prev);
      if (next.has(typeName)) next.delete(typeName);
      else next.add(typeName);
      return next;
    });
  };

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6">
          <div className="col-span-12">
            <div className="flex items-center h-10">
              <div className="text-base font-medium group-[.mode--light]:text-white">Trade In</div>
              <div className="ml-3 text-slate-500 dark:text-slate-400 text-sm">
                {loading ? "Loading..." : `${products.length} products`}
              </div>
            </div>

            <div className="flex flex-col gap-8 mt-3.5">
              <div className="flex flex-col box box--stacked">
                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                  <div className="flex flex-col sm:flex-row gap-x-2 gap-y-2 sm:ml-auto">
                    <Button
                      variant="success"
                      className="w-full sm:w-auto"
                      disabled={isSubmitting || !isEditing || !hasChanges}
                      onClick={() => void handleSubmit()}
                    >
                      Submit
                    </Button>
                    <Button
                      variant="warning"
                      className="w-full sm:w-auto"
                      disabled={isSubmitting}
                      onClick={() => {
                        setIsEditing(true);
                        setSuccess("");
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline-danger"
                      className="w-full sm:w-auto"
                      disabled={isSubmitting || !isEditing}
                      onClick={handleResetAll}
                    >
                      Reset All
                    </Button>
                  </div>
                </div>

                {error ? <div className="px-5 pb-3 text-danger text-sm">{error}</div> : null}
                {success ? <div className="px-5 pb-3 text-success text-sm">{success}</div> : null}

                <div className="max-w-full overflow-x-auto px-4 pb-4">
                  <Table className="border-b border-slate-200/60 text-xs table-auto w-max min-w-full">
                    <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                      <Table.Tr>
                        <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[240px]">
                          Product
                        </Table.Td>
                        <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[140px]">
                          SKU
                        </Table.Td>
                        <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 text-center min-w-[130px]">
                          Allow Trade-In?
                        </Table.Td>
                        <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 text-center min-w-[160px]">
                          Trade-In Value (RM)
                        </Table.Td>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {loading ? (
                        <Table.Tr>
                          <Table.Td className="py-4 text-slate-500" colSpan={COL_COUNT}>
                            Loading trade-in settings...
                          </Table.Td>
                        </Table.Tr>
                      ) : products.length === 0 ? (
                        <Table.Tr>
                          <Table.Td className="py-4 text-slate-500" colSpan={COL_COUNT}>
                            No products found.
                          </Table.Td>
                        </Table.Tr>
                      ) : (
                        groupedProducts.flatMap(([typeName, typeProducts]) => {
                          const expanded = expandedTypeNames.has(typeName);
                          const header = (
                            <Table.Tr key={`group-${typeName}`} className="bg-slate-100/70 dark:bg-darkmode-700/60">
                              <Table.Td
                                colSpan={COL_COUNT}
                                className="py-2 px-3 border-b border-slate-200/70 dark:border-darkmode-500"
                              >
                                <button
                                  type="button"
                                  className="w-full flex items-center gap-2 text-left"
                                  onClick={() => toggleType(typeName)}
                                >
                                  <Lucide
                                    icon={expanded ? "ChevronDown" : "ChevronRight"}
                                    className="w-4 h-4 text-slate-500"
                                  />
                                  <span className="font-medium">{typeName}</span>
                                  <span className="text-xs text-slate-500">({typeProducts.length})</span>
                                </button>
                              </Table.Td>
                            </Table.Tr>
                          );

                          if (!expanded) return [header];

                          const rows = typeProducts.map((p) => {
                            const rowIndex = rowIndexByProductId[p.id] ?? 0;
                            const allowed = Boolean(allowByProductId[p.id]);
                            const valueEnabled = isEditing && !isSubmitting && allowed;
                            return (
                              <Table.Tr key={p.id} className="[&_td]:border-b [&_td]:border-slate-200/60">
                                <Table.Td className="py-2 px-2 min-w-[240px] max-w-[320px] truncate">
                                  {p.name || "-"}
                                </Table.Td>
                                <Table.Td className="py-2 px-2 min-w-[140px] whitespace-nowrap text-slate-600 dark:text-slate-300">
                                  {p.sku || "-"}
                                </Table.Td>
                                <Table.Td className="py-2 px-2 text-center min-w-[130px]">
                                  <div className="flex justify-center">
                                    <FormCheck>
                                      <FormCheck.Input
                                        id={`allow-tradein-${p.id}`}
                                        type="checkbox"
                                        checked={allowed}
                                        disabled={!isEditing || isSubmitting}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setAllowByProductId((prev) => ({ ...prev, [p.id]: checked }));
                                          if (!checked) {
                                            setValuesByProductId((prev) => ({ ...prev, [p.id]: "0.00" }));
                                          }
                                        }}
                                      />
                                    </FormCheck>
                                  </div>
                                </Table.Td>
                                <Table.Td className="py-2 px-2 text-center tabular-nums min-w-[160px] bg-success/10 dark:bg-success/10">
                                  <FormInput
                                    id={inputId(rowIndex)}
                                    ref={(el) => {
                                      const id = inputId(rowIndex);
                                      if (el) inputRefs.current[id] = el;
                                      else delete inputRefs.current[id];
                                    }}
                                    inputMode="numeric"
                                    placeholder="0.00"
                                    className="w-[11ch] h-7 rounded-[0.5rem] text-center text-xs tabular-nums px-1.5"
                                    disabled={!valueEnabled}
                                    value={valuesByProductId[p.id] ?? "0.00"}
                                    onKeyDown={(e) => {
                                      if (!valueEnabled) return;
                                      if (e.key === "Tab" || e.key === "Enter") {
                                        const moved = focusVertical(rowIndex, e.shiftKey ? -1 : 1);
                                        if (moved) e.preventDefault();
                                        return;
                                      }
                                      if (/^\d$/.test(e.key) || e.key === "Backspace" || e.key === "Delete") {
                                        e.preventDefault();
                                        const nextValue = nextAtmMoneyValue(
                                          valuesByProductId[p.id] ?? "0.00",
                                          e.key
                                        );
                                        setValuesByProductId((prev) => ({ ...prev, [p.id]: nextValue }));
                                      }
                                    }}
                                    onFocus={(e) => e.currentTarget.select()}
                                    onChange={(e) => {
                                      if (!valueEnabled) return;
                                      const normalized = normalizeMoneyInput(e.target.value || "0");
                                      setValuesByProductId((prev) => ({
                                        ...prev,
                                        [p.id]: digitsToMoney(moneyToDigits(normalized)),
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      const normalized = normalizeMoneyInput(e.target.value);
                                      const v = Number(normalized || "0");
                                      setValuesByProductId((prev) => ({ ...prev, [p.id]: moneyToString(v) }));
                                    }}
                                  />
                                </Table.Td>
                              </Table.Tr>
                            );
                          });

                          return [header, ...rows];
                        })
                      )}
                    </Table.Tbody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}
